/**
 * /ndt-inspection/invoices (batch 5, fieldiq-api#1166). Two panels, full width:
 *
 *  READY TO INVOICE — `GET /ndt-inspection/invoices/billable`: every approved
 *  field ticket not yet on an invoice, grouped customer → job in the API's
 *  order. Tick tickets and press Create invoice (`{ ticket_ids }`), or
 *  "Invoice whole job" (`{ job_id }`). The API decides what may go on one
 *  invoice (one customer, one currency, one province or state, every line
 *  priced); a refusal is shown as a sentence (lib/ndt-invoices.ts), never a code.
 *
 *  INVOICES — `GET /ndt-inspection/invoices`, the NDI- register, unpaginated.
 *  Status is the API's filter and lives in the URL, so a record's back arrow
 *  returns to the list as it was left. A row opens /ndt-inspection/invoices/:id.
 *
 * Every figure is the API's: ticket subtotals (NULL → "No rate"), the job's
 * subtotal (the API's sum), invoice totals. Band counts are tallies of the
 * loaded, unpaginated lists. A new invoice opens on its page once generated.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ClipboardCheck, FileClock, Loader2, Receipt, Send } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useGenerateNdtInvoice, useNdtBillable, useNdtInvoices } from '../../hooks/useNdtInvoices';
import { fmtCurrency } from '../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { NDT_INVOICE_STATUS_SKIN, canWriteNdtInvoices, ndtInvoiceRefusal, ndtInvoiceStatusSkin } from '../../lib/ndt-invoices';
import { ndtTicketScopeText } from '../../lib/ndt-tickets';
import { NDT_INVOICE_STATUSES, type NdtBillableJob, type NdtInvoiceGenerate, type NdtInvoiceRow } from '../../types/ndt-invoices';
import { useOpenRecord } from '../downhole/recordPage';
import { NDT_PRIMARY, NDT_SECONDARY, NdtBadge, NdtChip, NdtChipRow, NdtEmpty, NdtPanel, NdtRegister, ndtDay, type NdtColumn } from './NdtBits';

const STATUS_CHIPS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  ...NDT_INVOICE_STATUSES.map((s) => ({ key: s, label: NDT_INVOICE_STATUS_SKIN[s].label })),
];

/** The billable jobs under the customer each belongs to, in the API's order. */
export function groupNdtBillableByCustomer(jobs: NdtBillableJob[]) {
  const groups: { customerId: string; customerName: string | null; jobs: NdtBillableJob[] }[] = [];
  for (const j of jobs) {
    let g = groups.find((x) => x.customerId === j.customerId);
    if (!g) {
      g = { customerId: j.customerId, customerName: j.customerName, jobs: [] };
      groups.push(g);
    }
    g.jobs.push(j);
  }
  return groups;
}

/** A subtotal the API holds as NULL is a ticket with an unpriced line: "No rate", never $0. */
const money = (v: number | null | undefined, currency: string | null | undefined) =>
  v === null || v === undefined ? 'No rate' : fmtCurrency(v, currency);

export const ndtInvoiceColumns: NdtColumn<NdtInvoiceRow>[] = [
  { key: 'number', header: 'Invoice #', render: (r) => <span className="font-mono font-extrabold text-white">{r.invoiceNumber || '—'}</span> },
  { key: 'customer', header: 'Customer', render: (r) => <span className="font-semibold text-white">{r.customerName || '—'}</span> },
  { key: 'job', header: 'Job', render: (r) => <span className="font-mono">{r.jobNumber || (r.jobId ? '—' : 'Several jobs')}</span> },
  { key: 'tickets', header: 'Tickets', render: (r) => r.ticketCount ?? '—' },
  { key: 'date', header: 'Invoice date', render: (r) => ndtDay(r.invoiceDate) },
  { key: 'due', header: 'Due', render: (r) => ndtDay(r.dueDate) },
  { key: 'total', header: 'Total', align: 'right', render: (r) => <span data-testid="ndt-invoice-row-total">{fmtCurrency(r.total, r.currency)}</span> },
  { key: 'status', header: 'Status', render: (r) => <NdtBadge skin={ndtInvoiceStatusSkin(r.status)} value={r.status} /> },
];

export default function NdtInvoicesScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const { user } = useAuth();
  const canWrite = canWriteNdtInvoices(user?.role);
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refusal, setRefusal] = useState<string | null>(null);

  const billable = useNdtBillable();
  const listQuery = useNdtInvoices({ status: status || undefined });
  const allQuery = useNdtInvoices();
  const generate = useGenerateNdtInvoice();

  const billableFailure = queryLoadFailure(billable);
  const billableJobs = useMemo(() => billable.data ?? [], [billable.data]);
  const groups = useMemo(() => groupNdtBillableByCustomer(billableJobs), [billableJobs]);
  const billableIds = useMemo(() => new Set(billableJobs.flatMap((j) => j.tickets.map((t) => t.ticketId))), [billableJobs]);
  // A ticket that left the billable list (invoiced elsewhere, voided) leaves the selection too.
  const picked = Array.from(selected).filter((id) => billableIds.has(id));

  const billableKnown = !billable.isLoading && !billableFailure;
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data;
  const countOf = (pick: (r: NdtInvoiceRow) => boolean) => (all ? all.filter(pick).length : undefined);
  const rows = listQuery.data ?? [];

  const setStatus = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set('status', value);
    else next.delete('status');
    setParams(next, { replace: true });
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const run = (body: NdtInvoiceGenerate) => {
    setRefusal(null);
    generate.mutate(body, {
      onSuccess: (inv) => {
        setSelected(new Set());
        toast.success(inv ? `${inv.invoiceNumber} created` : 'Invoice created');
        if (inv) openRecord(`/ndt-inspection/invoices/${inv.id}`);
      },
      onError: (err) => setRefusal(ndtInvoiceRefusal(err, 'The invoice was not created.').message),
    });
  };

  return (
    <div data-testid="ndt-invoices-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="Invoices"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <Link to="/finance/invoices?vertical=ndt_inspection" className="fi-action fi-action--standard fi-action--secondary" data-testid="ndt-all-invoices-link">
            All invoices
          </Link>
        }
        stats={[
          { icon: <ClipboardCheck className="size-4" aria-hidden="true" />, label: 'Ready', sub: 'Jobs to invoice', tone: 'accent', loading: billable.isLoading, value: displayValue(billableKnown ? billableJobs.length : undefined) },
          { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Tickets', sub: 'Approved, unbilled', tone: 'accent', loading: billable.isLoading, value: displayValue(billableKnown ? billableIds.size : undefined) },
          { icon: <FileClock className="size-4" aria-hidden="true" />, label: 'Drafts', sub: 'Not yet sent', tone: 'accent', loading: allQuery.isLoading, value: displayValue(countOf((r) => r.status === 'draft')) },
          { icon: <Send className="size-4" aria-hidden="true" />, label: 'Sent', sub: 'With the customer', tone: 'info', loading: allQuery.isLoading, value: displayValue(countOf((r) => r.status === 'sent')) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">An invoice bills approved field tickets: one customer, one currency, one province or state.</p>
          <NdtPanel
            eyebrow="Approved field tickets"
            title="Ready to invoice"
            testId="ndt-billable"
            actions={
              canWrite && billableJobs.length > 0 ? (
                <button type="button" className={NDT_PRIMARY} disabled={picked.length === 0 || generate.isPending} onClick={() => run({ ticket_ids: picked })} data-testid="ndt-generate-selected">
                  {generate.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Receipt className="size-4" aria-hidden="true" />}
                  {picked.length ? `Create invoice (${picked.length} ticket${picked.length === 1 ? '' : 's'})` : 'Create invoice'}
                </button>
              ) : undefined
            }
            footer={!canWrite && billableJobs.length > 0 ? 'Only an admin or office staff can create an invoice.' : undefined}
          >
            {refusal && (
              <p role="alert" data-testid="ndt-generate-refusal" className="mx-4 mt-4 rounded-fi-control border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text">
                {refusal}
              </p>
            )}
            {billable.isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading tickets ready to invoice" />
              </div>
            ) : billableFailure ? (
              <LoadFailureNotice error={queryFailureError(billable)} kind={billableFailure} subject="tickets ready to invoice" />
            ) : billableJobs.length === 0 ? (
              <NdtEmpty>No approved field tickets are waiting to be invoiced.</NdtEmpty>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left">
                  <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                    <tr className="border-l-4 border-l-transparent">
                      <th scope="col" className="w-[44px] px-4 py-3"><span className="sr-only">Select</span></th>
                      <th scope="col" className="px-4 py-3">Ticket #</th>
                      <th scope="col" className="px-4 py-3">Covers</th>
                      <th scope="col" className="px-4 py-3">PO</th>
                      <th scope="col" className="px-4 py-3 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  {groups.map((g) => (
                    <tbody key={g.customerId} data-testid="ndt-billable-customer">
                      <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/40">
                        <th scope="colgroup" colSpan={5} className="px-4 py-2 text-left text-sm font-extrabold text-white">
                          {g.customerName || '—'}
                        </th>
                      </tr>
                      {g.jobs.map((j) => (
                        <React.Fragment key={j.jobId}>
                          <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent" data-testid="ndt-billable-job">
                            <td colSpan={4} className="px-4 py-2">
                              <Link to={`/ndt-inspection/jobs/${j.jobId}`} className="font-mono text-sm font-bold text-fieldiq-text underline-offset-2 hover:underline">
                                {j.jobNumber || '—'}
                              </Link>
                              <span className="ml-2 text-xs text-fieldiq-muted">
                                {j.provinceState || 'Province or state not set'} · {j.tickets.length} ticket{j.tickets.length === 1 ? '' : 's'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex items-center justify-end gap-3">
                                <span className="font-mono text-sm tabular-nums text-fieldiq-text" data-testid="ndt-billable-job-subtotal">
                                  {money(j.subtotal, j.currency)}
                                </span>
                                {canWrite && (
                                  <button type="button" className={NDT_SECONDARY} disabled={generate.isPending} onClick={() => run({ job_id: j.jobId })} data-testid="ndt-generate-job">
                                    Invoice whole job
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {j.tickets.map((t) => (
                            <tr key={t.ticketId} className="border-t border-l-4 border-fieldiq-border border-l-fi-green" data-testid="ndt-billable-ticket">
                              <td className="px-4 py-2.5">
                                {canWrite && (
                                  <input
                                    type="checkbox"
                                    className="size-4 accent-fi-amber"
                                    checked={selected.has(t.ticketId)}
                                    onChange={() => toggle(t.ticketId)}
                                    aria-label={`Select ${t.ticketNumber}`}
                                    data-testid="ndt-billable-select"
                                  />
                                )}
                              </td>
                              <td className="px-4 py-2.5">
                                <button
                                  type="button"
                                  className="font-mono text-sm font-extrabold text-white underline-offset-2 hover:underline"
                                  onClick={() => openRecord(`/ndt-inspection/tickets/${t.ticketId}`)}
                                >
                                  {t.ticketNumber || '—'}
                                </button>
                              </td>
                              <td className="whitespace-nowrap px-4 py-2.5 text-sm text-fieldiq-text">{ndtTicketScopeText(t.scope, t.workDate)}</td>
                              <td className="px-4 py-2.5 font-mono text-sm text-fieldiq-text">{t.poNumber || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-sm tabular-nums text-white">{money(t.subtotal, t.currency)}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  ))}
                </table>
              </div>
            )}
          </NdtPanel>

          <NdtChipRow label="Filter by status">
            {STATUS_CHIPS.map((c) => (
              <NdtChip
                key={c.key || 'all'}
                label={c.label}
                active={status === c.key}
                count={all ? countOf((r) => !c.key || r.status === c.key) : undefined}
                onClick={() => setStatus(c.key)}
              />
            ))}
          </NdtChipRow>
          <NdtPanel
            eyebrow="Billed from field tickets"
            title="Invoices"
            testId="ndt-invoice-register"
            footer={!listQuery.isLoading && !queryLoadFailure(listQuery) && rows.length > 0 ? `${rows.length} invoice${rows.length === 1 ? '' : 's'}` : undefined}
          >
            <NdtRegister
              query={listQuery}
              rows={rows}
              columns={ndtInvoiceColumns}
              rail={(r) => ndtInvoiceStatusSkin(r.status).rail}
              onOpen={(r) => openRecord(`/ndt-inspection/invoices/${r.id}`)}
              subject="invoices"
              testId="ndt-invoice"
              minWidth="min-w-[1040px]"
              empty={status ? <NdtEmpty>No invoice has this status.</NdtEmpty> : <NdtEmpty>There are no NDT invoices yet.</NdtEmpty>}
              card={(r) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-extrabold text-white">{r.invoiceNumber || '—'}</p>
                      <p className="mt-0.5 truncate text-xs text-fieldiq-muted">
                        {r.jobNumber || '—'} · {r.customerName || '—'}
                      </p>
                    </div>
                    <NdtBadge skin={ndtInvoiceStatusSkin(r.status)} value={r.status} />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fieldiq-text">
                    <span>Due {ndtDay(r.dueDate)}</span>
                    <span className="font-mono">{fmtCurrency(r.total, r.currency)}</span>
                  </div>
                </>
              )}
            />
          </NdtPanel>
        </div>
      </section>
    </div>
  );
}
